import { Box, BoxProps } from '@mui/material';
import React, { useEffect, useState } from 'react';

interface ImageWrapperProps extends Omit<BoxProps, 'component'> {
  src: string;
  alt: string;
  onError?: (e: React.SyntheticEvent) => void;
  onLoad?: (e: React.SyntheticEvent) => void;
}

const isSvgUrl = (url: string | null | undefined): boolean => {
  if (!url) {
    return false;
  }
  const lowerCaseUrl = url.toLowerCase();
  return lowerCaseUrl.includes('.svg?');
};

export const ImageWrapper: React.FC<ImageWrapperProps> = ({ src, alt, onError, onLoad, sx, ...otherProps }) => {
  const [error, setError] = useState(false);
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isSvg = isSvgUrl(src);

  useEffect(() => {
    if (isSvg && src && !error) {
      let imageSrc = src;
      try {
        if (src.includes('%25')) {
          imageSrc = decodeURIComponent(src);
        }
      } catch (e) {
        // If decoding fails, use original src
      }

      setIsLoading(true);
      fetch(imageSrc, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        cache: 'default',
        headers: {
          Accept: 'image/svg+xml,application/xml;charset=UTF-8,application/xml,text/xml,*/*',
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then((text) => {
          setSvgContent(text);
          setIsLoading(false);
          onLoad?.({} as React.SyntheticEvent);
        })
        .catch(() => {
          setError(true);
          setIsLoading(false);
          onError?.({} as React.SyntheticEvent);
        });
    }
  }, [src, isSvg, error, onError, onLoad]);

  const handleError = (e: React.SyntheticEvent) => {
    setError(true);
    onError?.(e);
  };

  const handleLoad = (e: React.SyntheticEvent) => {
    onLoad?.(e);
  };

  if (isSvg && !error) {
    if (isLoading) {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...sx,
          }}
        >
          Loading...
        </Box>
      );
    }

    if (svgContent) {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& svg': {
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            },
            ...sx,
          }}
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />
      );
    }

    return null;
  }

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onError={handleError}
      onLoad={handleLoad}
      sx={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        ...sx,
      }}
      {...otherProps}
    />
  );
};
